import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import api, { fileUrl } from "../lib/api";
import { ORIENT_SECTIONS, ORIENT_STORE_NAME, sectionStoreName, splitName, productMeta } from "../lib/catalog";
import Header from "../components/Header";
import { ChevronLeft, Plus, Loader2 } from "lucide-react";

function ProductCard({ p, onAdd }) {
  const { t } = useApp();
  const [note, setNote] = useState("");
  const nm = splitName(p.name);
  const onSale = p.old_price && p.old_price > p.price;
  const img = p.image_path ? fileUrl(p.image_path) : null;
  const out = p.stock === 0;
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
      <div className="relative aspect-square overflow-hidden bg-accent grid place-items-center">
        {img ? <img src={img} alt={nm.sv} loading="lazy" className="h-full w-full object-cover" /> : <span className="text-4xl">{p.emoji || "🛍️"}</span>}
        {onSale && <span className="absolute top-2 start-2 px-2 py-0.5 rounded-full text-[11px] font-extrabold bg-destructive text-destructive-foreground">{t.rea}</span>}
        {out && <span className="absolute inset-0 bg-background/65 grid place-items-center"><span className="px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-foreground text-background">{t.outOfStock}</span></span>}
      </div>
      <div className="p-3 flex flex-col flex-1">
        <div className="font-semibold text-sm leading-tight line-clamp-2">{nm.sv}</div>
        {nm.ar && <div className="text-xs text-muted-foreground line-clamp-1" dir="rtl">{nm.ar}</div>}
        <div className="mt-1 flex items-baseline gap-2">
          <span className={`font-bold ${onSale ? "text-destructive" : "text-primary"}`}>{p.price} {t.kr}</span>
          {p.unit === "kg" && <span className="text-[11px] text-muted-foreground">/kg</span>}
          {onSale && <span className="text-xs text-muted-foreground line-through decoration-destructive/70">{p.old_price} {t.kr}</span>}
        </div>
        <input data-testid={`product-note-${p.key}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.note}
          className="mt-2 w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs outline-none focus:ring-2 focus:ring-primary/40" />
        <button data-testid={`product-add-${p.key}`} disabled={out} onClick={() => { if (out) return; onAdd(p, nm.sv, note); setNote(""); }}
          className={`mt-2 h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-opacity ${out ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-primary text-primary-foreground hover:opacity-90"}`}>
          <Plus className="h-4 w-4" /> {out ? t.outOfStock : t.add}
        </button>
      </div>
    </div>
  );
}

export default function StorePage() {
  const { t, lang, addToCart, search } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [seed, setSeed] = useState([]);
  const [limit, setLimit] = useState(48);

  const { data: storesData } = useQuery({ queryKey: ["stores"], queryFn: api.getStores });

  // Local seed is a safety fallback only (used if the backend catalog is unreachable).
  useEffect(() => {
    fetch("/orient_products.json").then((r) => r.json()).then(setSeed).catch(() => setSeed([]));
  }, []);

  // Map each Orient section to its backend store (named "Orient Livsmedel – <sv>").
  const sectionStores = useMemo(() => {
    const custom = storesData?.custom || [];
    return ORIENT_SECTIONS.map((sec) => {
      const st = custom.find((s) => s.name === sectionStoreName(sec.sv));
      return { slug: sec.slug, emoji: sec.emoji, storeId: st?.store_id || null };
    });
  }, [storesData]);

  const storeIdsKey = sectionStores.map((s) => s.storeId || "_").join(",");

  // Customer catalog reads live from the shared backend so admin edits (price, image,
  // hide/show) apply instantly. Only the named section stores are queried — the legacy
  // duplicate copies are never shown.
  const { data: backendItems = [], isLoading: beLoading } = useQuery({
    queryKey: ["orient-catalog", storeIdsKey],
    enabled: !!storesData,
    staleTime: 60000,
    queryFn: async () => {
      const withStore = sectionStores.filter((s) => s.storeId);
      const results = await Promise.all(
        withStore.map((s) =>
          api.getSectionProducts(s.storeId)
            .then((d) => ({ slug: s.slug, emoji: s.emoji, products: d?.products || [] }))
            .catch(() => ({ slug: s.slug, emoji: s.emoji, products: [] }))
        )
      );
      const out = [];
      results.forEach(({ slug, emoji, products }) => {
        products.forEach((p) => {
          if (p.deleted) return;
          const meta = productMeta(p);
          if (meta.status !== "active") return; // hidden from customers
          out.push({ key: `b_${p.id}`, section: slug, name: meta.clean, price: p.price, old_price: p.old_price, emoji: p.emoji || emoji, image_path: p.image_path, unit: "st", stock: meta.stock });
        });
      });
      return out;
    },
  });

  const seedItems = useMemo(
    () => seed.map((p, i) => ({ key: `s_${p.section}_${i}`, section: p.section, ...p, image_path: null, stock: null })),
    [seed]
  );

  const base = backendItems.length > 0 ? backendItems : seedItems;

  const active = params.get("sektion") || ORIENT_SECTIONS[0].slug;
  const setActive = (slug) => { setParams({ sektion: slug }); setLimit(48); };

  const q = (search || "").toLowerCase().trim();
  const items = useMemo(() => {
    if (q) return base.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 120);
    if (active === "erbjudanden") return base.filter((p) => p.section === "erbjudanden" || (p.old_price && p.old_price > p.price));
    return base.filter((p) => p.section === active);
  }, [q, active, base]);

  const add = (p, sv, note) => {
    addToCart({ key: `orient:${sv}:${note || ""}`, store_id: "orient", store_name: ORIENT_STORE_NAME, name: sv, price: p.price, old_price: p.old_price || null, image: p.image_path ? fileUrl(p.image_path) : null, emoji: p.emoji, qty: 1, note: note || "", age_restricted: false });
    toast.success(t.added, { description: sv });
  };

  const shown = q ? items : items.slice(0, limit);
  const loading = beLoading && base.length === 0;

  return (
    <div className="min-h-screen pb-10">
      <Header />
      <div className="relative h-28 sm:h-36 overflow-hidden bg-gradient-to-br from-primary to-primary/70">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-between py-4">
          <button data-testid="store-back-button" onClick={() => navigate("/")} className="self-start h-9 px-3 rounded-xl bg-white/20 backdrop-blur text-white text-sm font-semibold flex items-center gap-1 hover:bg-white/30"><ChevronLeft className="h-4 w-4 rtl:rotate-180" /> {t.back}</button>
          <div className="flex items-center gap-3 text-white">
            <img src="/orient-logo.png" alt="" className="h-12 w-12 rounded-xl bg-white p-1" />
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold drop-shadow">{ORIENT_STORE_NAME}</h1>
          </div>
        </div>
      </div>

      {!q && (
        <div className="sticky top-16 z-30 backdrop-blur-md bg-background/85 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar">
            {ORIENT_SECTIONS.map((s) => (
              <button key={s.slug} data-testid={`section-tab-${s.slug}`} onClick={() => setActive(s.slug)}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-2xl border text-sm font-semibold transition-all active:scale-95 ${active === s.slug ? "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20" : "bg-card border-border hover:border-primary/50"}`}>
                <span>{s.emoji}</span> {lang === "sv" ? s.sv : s.ar}
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="font-head text-lg font-bold mb-4">
          {q ? `${t.searchStores} “${search}”` : (lang === "sv" ? ORIENT_SECTIONS.find((s) => s.slug === active)?.sv : ORIENT_SECTIONS.find((s) => s.slug === active)?.ar)}
          <span className="text-muted-foreground font-normal text-sm"> · {items.length}</span>
        </h2>
        {loading ? (
          <div className="grid place-items-center py-20 text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : shown.length === 0 ? (
          <div className="text-center text-muted-foreground py-16">{t.noProducts}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {shown.map((p) => <ProductCard key={p.key} p={p} onAdd={add} />)}
          </div>
        )}
        {!q && items.length > limit && (
          <div className="text-center mt-6">
            <button data-testid="load-more-button" onClick={() => setLimit((l) => l + 48)} className="h-11 px-6 rounded-xl border border-border font-semibold hover:bg-accent">{lang === "sv" ? "Visa fler" : "عرض المزيد"}</button>
          </div>
        )}
      </main>
    </div>
  );
}
